"use client"

import { getConfigApi } from "@/lib/api/config"
import { ApiConfigResponse } from "@/definitions/types/api/config"
import {
  keepPreviousData,
  useQuery,
} from "@tanstack/react-query"
import {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react"
import LoadingSkeleton from "@/components/shared/LoadingSkeleton"
import { AxiosError } from "axios"
import { useDomain } from "@/hooks/useDomain"
import ApiConfigErrorPage from "@/components/shared/ApiConfigErrorPage"

const ConfigContext = createContext<
  ApiConfigResponse | undefined
>(undefined)

export function ConfigProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const domain = useDomain()
  const [imagePreloaded, setImagePreloaded] =
    useState(false)

  const configQuery = useQuery<
    ApiConfigResponse,
    AxiosError<ApiError>
  >({
    queryKey: ["api-config", domain],
    queryFn: () => getConfigApi(domain),
    placeholderData: keepPreviousData,
    staleTime: 1 * 60 * 60 * 1000,
    retry: 1,
    enabled: !!domain,
  })

  // Update favicon dynamically from API config
  useEffect(() => {
    const faviconUrl =
      configQuery.data?.visualPreferences?.layout?.images
        ?.favicon

    if (!faviconUrl) return

    const link =
      document.querySelector<HTMLLinkElement>("link[rel='icon']")
    if (link) {
      link.href = faviconUrl
    }
  }, [configQuery.data])

  // Preload background image as soon as config is available
  useEffect(() => {
    const backgroundImage =
      configQuery.data?.visualPreferences?.layout?.images
        ?.background

    if (!backgroundImage) {
      // No background image, mark as ready immediately
      setImagePreloaded(true)
      return
    }

    if (typeof window !== "undefined") {
      const img = new Image()
      img.onload = () => setImagePreloaded(true)
      img.onerror = () => setImagePreloaded(true) // Continue even if image fails
      img.src = backgroundImage
    } else {
      setImagePreloaded(true)
    }
  }, [configQuery.data])

  if (configQuery.error) {
    return <ApiConfigErrorPage error={configQuery.error} />
  }

  if (configQuery.isLoading && !configQuery.data) {
    return <LoadingSkeleton />
  }

  // Wait for background image to preload before rendering children
  if (!imagePreloaded) {
    return <LoadingSkeleton />
  }

  return (
    <ConfigContext.Provider value={configQuery.data}>
      {children}
    </ConfigContext.Provider>
  )
}

export const useAppConfig = () => {
  const context = useContext(ConfigContext)
  if (context === undefined) {
    throw new Error(
      "useAppConfig must be used within a ConfigProvider"
    )
  }
  return context
}
